import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { demoWorkflow, pets, faqs } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';

export default function Demo() {
  const [activeStep, setActiveStep] = useState(0);
  const [simState, setSimState] = useState('idle'); // idle, loading, success, error, empty

  const handleSimulate = (state) => {
    setSimState('loading');
    setTimeout(() => setSimState(state), 1200);
  };

  return (
    <div className="space-y-16 pb-20">
      {/* Overview Section */}
      <header className="text-center max-w-3xl mx-auto pt-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#0047AB]/5 rounded-full text-[#0047AB] text-xs font-bold uppercase tracking-widest mb-6 border border-[#0047AB]/10"
        >
          <SafeIcon icon="FiTarget" /> Mission Briefing
        </motion.div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-[#0047AB] tracking-tight mb-6">
          PetOrbit: Mission Control for Modern Pet Families
        </h1>
        <p className="text-slate-600 text-lg leading-relaxed">
          PetOrbit is the ultimate coordination hub for multi-caregiver households. 
          We solve the chaos of "Did the dog eat?" and "Whose turn is it to walk?" 
          by centralizing health tracking, scheduling, and sitter handovers into one 
          beautiful, mission-ready dashboard.
        </p>
      </header>

      {/* Interactive Workflow Section */}
      <section className="bg-white/80 backdrop-blur-xl rounded-[3rem] p-6 md:p-12 shadow-[0_20px_60px_rgba(0,0,0,0.06)] border border-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Operational Flow</h2>
            <p className="text-slate-500">Six steps to a perfectly coordinated pet orbit.</p>
          </div>
          <div className="flex gap-1 bg-[#F5EFE6] p-1 rounded-xl">
            {demoWorkflow.map((_, i) => (
              <div key={i} className={`h-1.5 w-6 rounded-full ${activeStep >= i ? 'bg-[#0047AB]' : 'bg-slate-200'}`} />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Steps List */}
          <div className="space-y-3">
            {demoWorkflow.map((item, idx) => (
              <motion.button
                key={item.step}
                onClick={() => setActiveStep(idx)}
                whileHover={{ x: 6 }}
                className={`w-full flex items-center gap-5 p-5 rounded-2xl border transition-all text-left group ${
                  activeStep === idx 
                    ? 'bg-[#0047AB] border-[#0047AB] text-white shadow-xl shadow-blue-200' 
                    : 'bg-white border-slate-100 hover:border-slate-300'
                }`}
              >
                <div className={`w-12 h-12 rounded-[1rem] flex items-center justify-center shrink-0 transition-colors ${
                  activeStep === idx ? 'bg-white/20' : 'bg-[#F5EFE6] text-[#0047AB]'
                }`}>
                  <SafeIcon icon={item.icon} className="text-2xl" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-sm md:text-base leading-tight">{item.title}</h3>
                  <p className={`text-[11px] mt-1 leading-normal opacity-80 ${
                    activeStep === idx ? 'text-blue-50' : 'text-slate-500'
                  }`}>
                    {item.description}
                  </p>
                </div>
                <SafeIcon icon="FiChevronRight" className={`transition-opacity ${activeStep === idx ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`} />
              </motion.button>
            ))}
          </div>

          {/* Live Preview Panel */}
          <div className="bg-[#F5EFE6] rounded-[2.5rem] p-8 md:p-12 border border-white/50 flex flex-col items-center justify-center text-center min-h-[450px] relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.1, y: -10 }}
                transition={{ duration: 0.4, ease: "backOut" }}
                className="w-full relative z-10"
              >
                <div className="inline-block px-4 py-1.5 bg-white rounded-full text-[10px] font-black text-[#FF7F50] uppercase tracking-widest mb-8 shadow-sm">
                  {demoWorkflow[activeStep].module} Interface
                </div>
                <h4 className="text-3xl font-bold text-slate-800 mb-6">{demoWorkflow[activeStep].title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed mb-10 max-w-sm mx-auto">
                  {demoWorkflow[activeStep].detail}
                </p>

                {/* Mock UI Element */}
                <div className="bg-white rounded-[2rem] p-6 shadow-[0_12px_40px_rgba(0,71,171,0.08)] border border-white max-w-[280px] mx-auto transform rotate-1">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#0047AB]">
                      <SafeIcon icon={demoWorkflow[activeStep].icon} />
                    </div>
                    <div className="text-left flex-1">
                      <div className="h-2 w-24 bg-slate-100 rounded-full mb-2" />
                      <div className="h-1.5 w-16 bg-slate-50 rounded-full" />
                    </div>
                  </div>
                  <div className="space-y-2 mb-6">
                    <div className="h-1.5 w-full bg-slate-50 rounded-full" />
                    <div className="h-1.5 w-3/4 bg-slate-50 rounded-full" />
                  </div>
                  <button className="w-full py-3 bg-[#FF7F50] text-white rounded-xl text-xs font-bold shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-[0.98] transition-transform">
                    Execute Protocol
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
            
            {/* Background Decoration */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
               <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
               <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-200 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            </div>
          </div>
        </div>
      </section>

      {/* Simulated States Section */}
      <section className="space-y-8">
        <div className="text-center md:text-left">
          <h2 className="text-2xl font-bold text-slate-800">System State Simulation</h2>
          <p className="text-slate-500">Test how the Mission Control responds to various uplink scenarios.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="flex md:flex-col flex-wrap gap-3">
            {[
              { id: 'loading', label: 'Test Loading', icon: 'FiRefreshCw' },
              { id: 'success', label: 'Test Success', icon: 'FiCheckCircle' },
              { id: 'error', label: 'Test Warning', icon: 'FiAlertTriangle' },
              { id: 'empty', label: 'Test Empty', icon: 'FiInbox' }
            ].map(btn => (
              <button 
                key={btn.id}
                onClick={() => handleSimulate(btn.id)}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-bold text-xs transition-all border ${
                  simState === btn.id 
                    ? 'bg-[#0047AB] text-white border-[#0047AB] shadow-lg shadow-blue-200' 
                    : 'bg-white text-slate-600 border-slate-100 hover:border-slate-300'
                }`}
              >
                <SafeIcon icon={btn.icon} />
                {btn.label}
              </button>
            ))}
          </div>

          <div className="md:col-span-3 bg-white/60 backdrop-blur-sm rounded-[2.5rem] border border-white p-10 min-h-[300px] flex flex-col items-center justify-center text-center shadow-inner">
            <AnimatePresence mode="wait">
              {simState === 'idle' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-slate-400">
                  <SafeIcon icon="FiActivity" className="text-5xl mx-auto mb-4 opacity-20" />
                  <p className="font-medium italic">Select a system state to simulate...</p>
                </motion.div>
              )}
              
              {simState === 'loading' && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }} 
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  className="space-y-6"
                >
                  <motion.div 
                    animate={{ rotate: 360 }} 
                    transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    className="text-5xl text-[#0047AB]"
                  >
                    <SafeIcon icon="FiLoader" />
                  </motion.div>
                  <div className="space-y-2">
                    <h4 className="text-xl font-bold text-[#0047AB]">Synchronizing Uplink...</h4>
                    <p className="text-slate-500 text-sm">Validating cargo inventory and crew vitals.</p>
                  </div>
                </motion.div>
              )}

              {simState === 'success' && (
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="space-y-4">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-4xl mx-auto shadow-xl shadow-green-100">
                    <SafeIcon icon="FiCheck" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-800">Mission Parameters Saved</h4>
                  <p className="text-slate-500 max-w-xs">All pet parents have been notified of the protocol update.</p>
                </motion.div>
              )}

              {simState === 'error' && (
                <motion.div initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="space-y-4">
                  <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-4xl mx-auto shadow-xl shadow-red-100">
                    <SafeIcon icon="FiAlertTriangle" />
                  </div>
                  <h4 className="text-2xl font-bold text-red-800">Connection Interrupted</h4>
                  <p className="text-slate-500 max-w-xs">Verify your local supply uplink and try again.</p>
                </motion.div>
              )}

              {simState === 'empty' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  <div className="text-6xl text-slate-200 mx-auto">
                    <SafeIcon icon="FiInbox" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-400">No Mission Logs Found</h4>
                  <p className="text-slate-400 text-sm">Start by adding a new task to your crew's timeline.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-800">Crew Support FAQ</h2>
          <p className="text-slate-500">Answers to common questions about orbital pet management.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {faqs.map((faq, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <h4 className="font-bold text-[#0047AB] mb-3 flex gap-2">
                <span className="text-[#FF7F50]">Q:</span> {faq.q}
              </h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                {faq.a}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Credits */}
      <footer className="text-center pt-10 border-t border-white/40">
        <p className="text-sm font-medium text-slate-400 uppercase tracking-widest">PetOrbit System v2.5.0</p>
        <div className="mt-2 text-xs text-slate-500 bg-white/50 backdrop-blur-sm inline-block px-4 py-2 rounded-full border border-slate-200">
          Made by LingLink AI and DigLab AI
        </div>
      </footer>
    </div>
  );
}